import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input, Select } from './FormControls'
import { MODULE_REGISTRY } from '../lib/quantityEngine'
import RebarSlabModulePanel from './RebarSlabModulePanel'
import RebarBeamModulePanel from './RebarBeamModulePanel'
import RebarColumnModulePanel from './RebarColumnModulePanel'
import RebarFoundationModulePanel from './RebarFoundationModulePanel'
import RebarWallModulePanel from './RebarWallModulePanel'
import ModuleExplanationPanel from './ModuleExplanationPanel'
import DedicatedModuleWrapper from './DedicatedModuleWrapper'
import SurfaceAreaModulePanel from './SurfaceAreaModulePanel'
import ConcreteVolumeModulePanel from './ConcreteVolumeModulePanel'
import FormworkModulePanel from './FormworkModulePanel'
import ExcavationModulePanel from './ExcavationModulePanel'
import MepEquipmentModulePanel from './MepEquipmentModulePanel'
import PipeworkModulePanel from './PipeworkModulePanel'
import ContainmentModulePanel from './ContainmentModulePanel'
import DuctworkModulePanel from './DuctworkModulePanel'
import CablesModulePanel from './CablesModulePanel'
import DisposalModulePanel from './DisposalModulePanel'
import CompactionModulePanel from './CompactionModulePanel'
import SheetMetalModulePanel from './SheetMetalModulePanel'
import DoorsWindowsModulePanel from './DoorsWindowsModulePanel'
import FacadeModulePanel from './FacadeModulePanel'
import MiscellaneousModulePanel from './MiscellaneousModulePanel'

// BOQ INTEGRATION: all quantity-engineering modules follow the EXACT SAME
// path as Rebar — recapture_quantity_source() via onSave(payload), no new
// mechanism. Group 4 (MEP) modules join here identically to the Concrete
// & Earthwork family before them.
const DEDICATED_PANEL_COMPONENTS = {
  rebar_slab: RebarSlabModulePanel, rebar_beam: RebarBeamModulePanel, rebar_column: RebarColumnModulePanel,
  rebar_foundation: RebarFoundationModulePanel, rebar_wall: RebarWallModulePanel,
  surface_area: SurfaceAreaModulePanel, concrete_volume: ConcreteVolumeModulePanel,
  formwork: FormworkModulePanel, excavation: ExcavationModulePanel,
  mep_equipment: MepEquipmentModulePanel, pipework: PipeworkModulePanel, containment: ContainmentModulePanel,
  ductwork: DuctworkModulePanel, cables: CablesModulePanel,
  disposal: DisposalModulePanel, compaction: CompactionModulePanel, sheet_metal: SheetMetalModulePanel,
  doors_windows: DoorsWindowsModulePanel, facade: FacadeModulePanel, miscellaneous: MiscellaneousModulePanel,
}

// =======================================================================
// SpecializedModulePanel — the ONE generic renderer for any
// ModuleDefinition in MODULE_REGISTRY. Two geometry modes are supported:
//   'length' | 'area' | 'count'  -> single takeoff-item picker (Phase 3)
//   'rectangle_multi'            -> multi-select of Rectangle/Area items,
//                                    each resolved to real Length x Width
//                                    via geometry_data + its calibration
//                                    (Rebar Slab, Phase 4)
// Adding a future module never requires touching this file for simple
// (length/area/count) geometry — only 'rectangle_multi' consumers need
// the dedicated slab-geometry UI below, which itself remains generic to
// any module using that geometry source (not Slab-specific by name).
//
// STRICT RULE: this component never computes a quantity itself. It only
// collects geometry + engineering inputs, calls the module's pure
// calculate() function, and displays the result. Persistence goes
// exclusively through the existing recapture_quantity_source() RPC.
// =======================================================================

const BAR_DIAMETERS = [8, 10, 12, 16, 20, 25, 32, 40]

export default function SpecializedModulePanel({ boqItem, tenderId, onApplied, onError }) {
  // LOCKED (legacy cleanup, unaffected by BOQ integration approval):
  // test_linear_quantity (stored as generic_area_x_factor historically)
  // is a legacy proof-of-concept module — hidden from new selection here
  // regardless of any other module's BOQ status. Its MODULE_REGISTRY
  // entry, calculate(), and historical resource_quantity_output rows
  // remain completely untouched.
  const LEGACY_HIDDEN_MODULES = ['test_linear_quantity']
  const moduleKeys = Object.keys(MODULE_REGISTRY).filter((k) => !LEGACY_HIDDEN_MODULES.includes(k))
  const [selectedModuleKey, setSelectedModuleKey] = useState(moduleKeys[0] || '')
  const moduleDef = MODULE_REGISTRY[selectedModuleKey]
  const isMultiRect = moduleDef?.geometrySource === 'rectangle_multi'

  const [tenderIdForRebar, setTenderIdForRebar] = useState(tenderId)
  const [rebarLinkedItems, setRebarLinkedItems] = useState(null)
  const [rebarSaving, setRebarSaving] = useState(false)

  useEffect(() => {
    if (!moduleDef?.isDedicatedPanel) return
    supabase.from('takeoff_item').select('id, description, unit, measurement_type').eq('tender_id', tenderId)
      .then(({ data, error }) => { if (!error) setRebarLinkedItems(data || []) })
  }, [moduleDef, tenderId])

  // ---- simple single-geometry state (Phase 3 modules) ----
  const [takeoffItems, setTakeoffItems] = useState(null)
  const [selectedTakeoffItemId, setSelectedTakeoffItemId] = useState('')
  const [aggregatedQuantity, setAggregatedQuantity] = useState(null)
  const [inputs, setInputs] = useState({})

  // ---- multi-rectangle geometry state (Rebar Slab and future modules) ----
  const [rectItems, setRectItems] = useState(null) // [{id, description, unit}]
  const [selectedSlabIds, setSelectedSlabIds] = useState(new Set())
  const [resolvedSlabs, setResolvedSlabs] = useState({}) // id -> {label, length, width, area} | {id, error}
  const [layers, setLayers] = useState([])
  const [covers, setCovers] = useState({ top: '', bottom: '' })
  const [lapPct, setLapPct] = useState('')
  const [wastePct, setWastePct] = useState('')

  const [applying, setApplying] = useState(false)

  // Reset everything when the selected module changes.
  useEffect(() => {
    setSelectedTakeoffItemId(''); setAggregatedQuantity(null); setInputs({})
    setSelectedSlabIds(new Set()); setResolvedSlabs({}); setLayers([]); setCovers({ top: '', bottom: '' })
    setLapPct(''); setWastePct('')

    if (!moduleDef) return
    if (isMultiRect) {
      // Rectangle/area takeoff items are candidate slab geometries.
      supabase
        .from('takeoff_item')
        .select('id, description, unit, measurement_type')
        .eq('tender_id', tenderId)
        .eq('measurement_type', 'area')
        .then(({ data, error }) => { if (error) onError(error.message); else setRectItems(data || []) })
    } else if (moduleDef.geometrySource !== 'none') {
      supabase
        .from('takeoff_item')
        .select('id, description, unit, measurement_type')
        .eq('tender_id', tenderId)
        .eq('measurement_type', moduleDef.geometrySource)
        .then(({ data, error }) => { if (error) onError(error.message); else setTakeoffItems(data || []) })
    } else {
      setTakeoffItems([])
    }
  }, [selectedModuleKey, tenderId, moduleDef, isMultiRect, onError])

  // ---- simple single geometry: fetch aggregated quantity ----
  useEffect(() => {
    if (isMultiRect || !selectedTakeoffItemId) { setAggregatedQuantity(null); return }
    supabase
      .from('takeoff_item_aggregated_quantity')
      .select('aggregated_quantity')
      .eq('takeoff_item_id', selectedTakeoffItemId)
      .maybeSingle()
      .then(({ data, error }) => { if (error) onError(error.message); else setAggregatedQuantity(data?.aggregated_quantity ?? 0) })
  }, [selectedTakeoffItemId, isMultiRect, onError])

  // ---- multi-rect: resolve real Length/Width for each newly-selected slab ----
  useEffect(() => {
    if (!isMultiRect) return
    const idsToResolve = [...selectedSlabIds].filter((id) => !resolvedSlabs[id])
    if (idsToResolve.length === 0) return

    ;(async () => {
      for (const id of idsToResolve) {
        const item = rectItems.find((r) => r.id === id)
        // Use the most recent active measurement for this takeoff item —
        // geometry_data.pdf_width/pdf_height are recovered exactly as
        // captured, and the calibration used for THAT measurement (not
        // the sheet's current calibration) is used to derive real units.
        const { data: measurements, error } = await supabase
          .from('measurement')
          .select('geometry_data, calibration_id')
          .eq('takeoff_item_id', id)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1)
        if (error) { onError(error.message); continue }
        const m = measurements?.[0]
        if (!m || m.geometry_data?.type !== 'rectangle') {
          setResolvedSlabs((prev) => ({ ...prev, [id]: { error: 'No Rectangle measurement found for this item — Length/Width cannot be recovered.' } }))
          continue
        }
        const { data: cal, error: calError } = await supabase
          .from('sheet_calibration')
          .select('real_distance, pixel_distance')
          .eq('id', m.calibration_id)
          .single()
        if (calError || !cal) {
          setResolvedSlabs((prev) => ({ ...prev, [id]: { error: 'Calibration used for this measurement could not be found.' } }))
          continue
        }
        const scaleFactor = cal.real_distance / cal.pixel_distance
        const length = Number(m.geometry_data.pdf_width) * scaleFactor
        const width = Number(m.geometry_data.pdf_height) * scaleFactor
        setResolvedSlabs((prev) => ({ ...prev, [id]: { label: item?.description || id, length, width, area: length * width } }))
      }
    })()
  }, [selectedSlabIds, isMultiRect, rectItems, resolvedSlabs, onError])

  function toggleSlab(id) {
    setSelectedSlabIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function addLayer() {
    setLayers((prev) => [...prev, { id: `L${Date.now()}`, label: '', direction: 'X', diameter: '', spacing: '', coverPosition: 'bottom' }])
  }
  function updateLayer(id, patch) {
    setLayers((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)))
  }
  function removeLayer(id) {
    setLayers((prev) => prev.filter((l) => l.id !== id))
  }

  const geometry = useMemo(() => {
    if (isMultiRect) {
      const slabs = [...selectedSlabIds]
        .map((id) => resolvedSlabs[id])
        .filter((s) => s && !s.error)
      return { slabs }
    }
    return { aggregatedQuantity }
  }, [isMultiRect, selectedSlabIds, resolvedSlabs, aggregatedQuantity])

  const moduleInputs = useMemo(() => {
    if (isMultiRect) {
      return {
        layers: layers.map((l) => ({ ...l, diameter: l.diameter === '' ? NaN : Number(l.diameter), spacing: l.spacing === '' ? NaN : Number(l.spacing) })),
        covers: { top: covers.top === '' ? NaN : Number(covers.top), bottom: covers.bottom === '' ? NaN : Number(covers.bottom) },
        lapPct, wastePct,
      }
    }
    return inputs
  }, [isMultiRect, layers, covers, lapPct, wastePct, inputs])

  const result = useMemo(() => {
    if (!moduleDef) return null
    try {
      return { value: moduleDef.calculate(geometry, moduleInputs), error: null }
    } catch (e) {
      return { value: null, error: e.message }
    }
  }, [moduleDef, geometry, moduleInputs])

  const hasUnresolvedSlabError = isMultiRect && [...selectedSlabIds].some((id) => resolvedSlabs[id]?.error)

  async function handleApply() {
    // Same hard guard as the Quantification path: this generic handler
    // must be structurally impossible to run for a dedicated module —
    // dedicated modules have no `calculate()` in the registry and save
    // exclusively through their own onSave(payload) below.
    if (moduleDef?.isDedicatedPanel) {
      onError('Internal error: the generic apply handler cannot be used for a dedicated engineering module. Use that module\'s own Save Output button.')
      return
    }
    if (!result?.value) return
    setApplying(true)
    const { data: authData } = await supabase.auth.getUser()
    const { error } = await supabase.rpc('recapture_quantity_source', {
      p_boq_item_id: boqItem.id,
      p_source_type: 'takeoff_derived',
      p_captured_value: result.value.finalQuantity,
      p_takeoff_item_id: isMultiRect ? [...selectedSlabIds][0] || null : selectedTakeoffItemId,
      p_captured_by: authData.user?.id,
      p_module_key: moduleDef.moduleKey,
    })
    setApplying(false)
    if (error) onError(error.message)
    else onApplied()
  }

  if (moduleKeys.length === 0) {
    return <div style={{ fontSize: 12, color: colors.textMuted, padding: '20px 0', textAlign: 'center' }}>No specialized modules are available yet.</div>
  }

  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 5 }}>Module</label>
      <Select value={selectedModuleKey} onChange={(e) => setSelectedModuleKey(e.target.value)} style={{ width: '100%', fontSize: 13, marginBottom: 14 }}>
        {moduleKeys.map((k) => <option key={k} value={k}>{MODULE_REGISTRY[k].label}</option>)}
      </Select>

      {moduleDef?.isDedicatedPanel ? (
        (() => {
          // Same persistence adapter shared by every dedicated Rebar
          // module here — only p_module_key differs, no duplicated RPC
          // logic per module.
          const savePayloadToBoq = async (payload) => {
            setRebarSaving(true)
            const { data: authData } = await supabase.auth.getUser()
            const { error } = await supabase.rpc('recapture_quantity_source', {
              p_boq_item_id: boqItem.id,
              p_source_type: 'takeoff_derived',
              p_captured_value: payload.calculatedValueTon,
              p_takeoff_item_id: payload.takeoffItemIdForProvenance,
              p_captured_by: authData.user?.id,
              p_module_key: moduleDef.moduleKey,
            })
            setRebarSaving(false)
            if (error) { onError(error.message); return false }
            onApplied()
            return true
          }
          const Panel = DEDICATED_PANEL_COMPONENTS[moduleDef.moduleKey] || RebarSlabModulePanel
          return (
            <DedicatedModuleWrapper moduleKey={moduleDef.moduleKey}>
              <Panel
                boqItemId={boqItem.id}
                linkedItems={rebarLinkedItems || []}
                superseding={null}
                onClose={() => {}}
                onError={onError}
                saving={rebarSaving}
                onSave={savePayloadToBoq}
              />
            </DedicatedModuleWrapper>
          )
        })()
      ) : isMultiRect ? (
        <>
          {/* ===== Multi-slab geometry selection ===== */}
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 5 }}>
            Geometry — Select Slab(s)
          </label>
          <div style={{ border: `1px solid ${colors.border}`, borderRadius: 6, maxHeight: 140, overflowY: 'auto', marginBottom: 8 }}>
            {(rectItems || []).length === 0 && <div style={{ padding: 10, fontSize: 12, color: colors.textMuted }}>No Area/Rectangle takeoff items found for this tender.</div>}
            {(rectItems || []).map((item) => {
              const resolved = resolvedSlabs[item.id]
              return (
                <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderBottom: `1px solid ${colors.border}`, fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedSlabIds.has(item.id)} onChange={() => toggleSlab(item.id)} />
                  <span style={{ flex: 1 }}>{item.description}</span>
                  {selectedSlabIds.has(item.id) && resolved && (
                    resolved.error
                      ? <span style={{ color: colors.danger, fontSize: 10 }}>{resolved.error}</span>
                      : <span style={{ color: colors.textMuted, fontSize: 11 }}>{formatNumber(resolved.length)} × {formatNumber(resolved.width)} m = {formatNumber(resolved.area)} m²</span>
                  )}
                </label>
              )
            })}
          </div>
          {selectedSlabIds.size > 0 && !hasUnresolvedSlabError && (
            <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 14 }}>
              Total Area (reference only — each slab is still calculated independently): {' '}
              <strong>{formatNumber([...selectedSlabIds].reduce((s, id) => s + (resolvedSlabs[id]?.area || 0), 0))} m²</strong>
            </div>
          )}

          {/* ===== Covers ===== */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 5 }}>Top Cover (mm) — required</label>
              <Input type="number" step="any" value={covers.top} onChange={(e) => setCovers((p) => ({ ...p, top: e.target.value }))} style={{ width: '100%', fontSize: 13 }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 5 }}>Bottom Cover (mm) — required</label>
              <Input type="number" step="any" value={covers.bottom} onChange={(e) => setCovers((p) => ({ ...p, bottom: e.target.value }))} style={{ width: '100%', fontSize: 13 }} />
            </div>
          </div>

          {/* ===== Dynamic reinforcement layers ===== */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>Reinforcement Layers</label>
            <Button size="sm" variant="secondary" onClick={addLayer}>+ Add Layer</Button>
          </div>
          {layers.length === 0 && <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 14 }}>No layers defined yet — add at least one.</div>}
          {layers.map((l) => (
            <div key={l.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 10, marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <Input placeholder="Layer name, e.g. Bottom Main X" value={l.label} onChange={(e) => updateLayer(l.id, { label: e.target.value })} style={{ flex: 1, fontSize: 12 }} />
                <button onClick={() => removeLayer(l.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}>✕</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                <Select value={l.direction} onChange={(e) => updateLayer(l.id, { direction: e.target.value })} style={{ fontSize: 12 }}>
                  <option value="X">Direction X</option>
                  <option value="Y">Direction Y</option>
                </Select>
                <Select value={l.diameter} onChange={(e) => updateLayer(l.id, { diameter: e.target.value })} style={{ fontSize: 12 }}>
                  <option value="" disabled>Ø (mm)</option>
                  {BAR_DIAMETERS.map((d) => <option key={d} value={d}>Ø{d}</option>)}
                </Select>
                <Input type="number" placeholder="Spacing (mm)" value={l.spacing} onChange={(e) => updateLayer(l.id, { spacing: e.target.value })} style={{ fontSize: 12 }} />
                <Select value={l.coverPosition} onChange={(e) => updateLayer(l.id, { coverPosition: e.target.value })} style={{ fontSize: 12 }}>
                  <option value="top">Top Cover</option>
                  <option value="bottom">Bottom Cover</option>
                </Select>
              </div>
            </div>
          ))}

          {/* ===== Lap / Waste ===== */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10, marginBottom: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 5 }}>Lap/Splice % — required</label>
              <Input type="number" step="any" value={lapPct} onChange={(e) => setLapPct(e.target.value)} style={{ width: '100%', fontSize: 13 }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 5 }}>Waste % — required</label>
              <Input type="number" step="any" value={wastePct} onChange={(e) => setWastePct(e.target.value)} style={{ width: '100%', fontSize: 13 }} />
            </div>
          </div>

          {/* ===== Full auditable breakdown ===== */}
          {result?.value && (
            <div style={{ marginBottom: 14 }}>
              {result.value.slabBreakdown.map((sb, si) => (
                <div key={si} style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10, marginBottom: 8, background: '#FAFBFC' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: colors.textPrimary, marginBottom: 6 }}>
                    {sb.slab.label} — {formatNumber(sb.slab.length)} × {formatNumber(sb.slab.width)} m ({formatNumber(sb.slab.area)} m²)
                  </div>
                  {sb.layerResults.map((lr, li) => (
                    <div key={li} style={{ fontSize: 11, color: colors.textSecondary, padding: '4px 0', borderTop: li > 0 ? `1px dashed ${colors.border}` : 'none' }}>
                      <div style={{ fontWeight: 700, color: colors.textPrimary }}>{lr.layer.label || `Layer ${li + 1}`} (Dir {lr.layer.direction}, Ø{lr.layer.diameter}mm @ {lr.layer.spacing}mm, {lr.layer.coverPosition} cover {lr.cover}mm)</div>
                      <div>Effective Dim: {formatNumber(lr.effectiveDimension)} m · Spaces: {lr.numberOfSpaces} · Bars: {lr.numberOfBars} · Cutting Length: {formatNumber(lr.cuttingLength)} m</div>
                      <div>Total Length: {formatNumber(lr.totalLength)} m · Unit Weight: {formatNumber(lr.unitWeight)} kg/m · <strong style={{ color: colors.textPrimary }}>Gross: {formatNumber(lr.grossWeight)} kg</strong></div>
                    </div>
                  ))}
                  <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${colors.border}`, fontSize: 12, fontWeight: 700 }}>Slab Gross: {formatNumber(sb.slabGross)} kg</div>
                </div>
              ))}

              <div style={{ padding: '10px 12px', background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8 }}>
                {result.value.steps.map((s, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
                    <span style={{ color: colors.textSecondary }}>{s.label}</span>
                    <span style={{ fontWeight: 600, color: colors.textPrimary }}>{formatNumber(s.value)} {s.unit}</span>
                  </div>
                ))}
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${colors.border}` }}>
                  <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase' }}>Final Reinforcement Weight</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: colors.primary }}>
                    {formatNumber(result.value.finalQuantityKg)} kg &nbsp;=&nbsp; {formatNumber(result.value.finalQuantity)} ton
                  </div>
                </div>
              </div>
            </div>
          )}
          {result?.error && <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 14, fontStyle: 'italic' }}>{result.error}</div>}
        </>
      ) : (
        <>
          <ModuleExplanationPanel explanation={moduleDef.explanation} />
          {moduleDef.geometrySource !== 'none' && (
            <>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 5 }}>
                Geometry — {moduleDef.geometrySource} takeoff item
              </label>
              <Select value={selectedTakeoffItemId} onChange={(e) => setSelectedTakeoffItemId(e.target.value)} style={{ width: '100%', fontSize: 13, marginBottom: 14 }}>
                <option value="" disabled>Select a {moduleDef.geometrySource} takeoff item…</option>
                {(takeoffItems || []).map((t) => <option key={t.id} value={t.id}>{t.description} ({t.unit})</option>)}
              </Select>
              {selectedTakeoffItemId && aggregatedQuantity != null && (
                <div style={{ fontSize: 12, color: colors.textMuted, marginTop: -10, marginBottom: 14 }}>
                  Measured: <strong style={{ color: colors.textPrimary }}>{formatNumber(aggregatedQuantity)}</strong>
                </div>
              )}
            </>
          )}

          {moduleDef.inputFields.map((field) => (
            <div key={field.key} style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 5 }}>
                {field.label} {field.required && '— required'}
              </label>
              <Input
                type={field.type} step="any"
                value={inputs[field.key] ?? ''}
                onChange={(e) => setInputs((prev) => ({ ...prev, [field.key]: e.target.value === '' ? '' : Number(e.target.value) }))}
                style={{ width: '100%', fontSize: 13 }}
              />
            </div>
          ))}

          {result?.value && (
            <div style={{ marginTop: 4, marginBottom: 14, padding: '10px 12px', background: '#FAFBFC', border: `1px solid ${colors.border}`, borderRadius: 8 }}>
              <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase', marginBottom: 6 }}>Calculation Breakdown</div>
              {result.value.steps.map((s, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
                  <span style={{ color: colors.textSecondary }}>{s.label}</span>
                  <span style={{ fontWeight: 600, color: colors.textPrimary }}>{formatNumber(s.value)} {s.unit}</span>
                </div>
              ))}
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${colors.border}` }}>
                <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase' }}>Final Quantity</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: colors.primary }}>{formatNumber(result.value.finalQuantity)} {result.value.finalUnit}</div>
              </div>
            </div>
          )}
          {result?.error && <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 14, fontStyle: 'italic' }}>{result.error}</div>}
        </>
      )}

      {!moduleDef?.isDedicatedPanel && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleApply} disabled={!result?.value || applying || hasUnresolvedSlabError}>
            {applying ? 'Applying…' : 'Apply Quantity'}
          </Button>
        </div>
      )}
    </div>
  )
}
